import React, { useState, useEffect, useCallback } from 'react';
import { X, Minus, Plus, Trash2, Loader2, CheckCircle, MapPin, Truck, AlertCircle, RefreshCw, Package } from 'lucide-react';
import { OrderItem, Product, Address, ShippingQuote, ShippingMethod } from '../types';
import { createOrder } from '../services/orderService';
import { getShippingRates, LOCAL_GTA_RATE, isGTAAddress } from '../services/shippingService';
import { sendOrderConfirmation } from '../services/emailService';
import { RetailerUser } from '../services/authService';
import { BUSINESS } from '../constants';

interface CheckoutModalProps {
  isOpen: boolean;
  onClose: () => void;
  cartItems: OrderItem[];
  products: Product[];
  retailerUser: RetailerUser;
  onUpdateQuantity: (productId: string, quantity: number) => void;
  onRemoveItem: (productId: string) => void;
  onClearCart: () => void;
}

const CheckoutModal: React.FC<CheckoutModalProps> = ({
  isOpen, onClose, cartItems, products, retailerUser,
  onUpdateQuantity, onRemoveItem, onClearCart
}) => {
  const [step, setStep] = useState<'cart' | 'shipping' | 'confirm'>('cart');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [orderError, setOrderError] = useState('');
  const [orderNumber, setOrderNumber] = useState('');
  const [address, setAddress] = useState<Address>({
    street: '', city: '', province: 'ON', postalCode: '', country: 'Canada'
  });

  // Shipping state
  const [shippingMethod, setShippingMethod] = useState<ShippingMethod | null>(null);
  const [shippingQuotes, setShippingQuotes] = useState<ShippingQuote[]>([]);
  const [selectedQuote, setSelectedQuote] = useState<ShippingQuote | null>(null);
  const [isFetchingRates, setIsFetchingRates] = useState(false);
  const [shippingError, setShippingError] = useState('');

  const isAddressComplete = !!(address.street && address.city && address.province && address.postalCode);

  const handleFetchRates = useCallback(async () => {
    if (!isAddressComplete) {
      setShippingError('Please fill in your shipping address first.');
      return;
    }

    setIsFetchingRates(true);
    setShippingError('');

    try {
      const result = await getShippingRates(address, cartItems, products);

      if (result.error || result.quotes.length === 0) {
        setShippingError(result.error || 'No shipping options available for this address.');
        setShippingMethod(null);
      } else {
        setShippingQuotes(result.quotes);
        const cheapest = result.quotes.reduce((a, b) => a.price < b.price ? a : b);
        setSelectedQuote(cheapest);
        setShippingMethod('eshipper');
      }
    } catch (err: any) {
      setShippingError('Failed to fetch shipping rates. Please try again.');
    } finally {
      setIsFetchingRates(false);
    }
  }, [address, cartItems, products, isAddressComplete]);

  // Reset state when modal closes
  useEffect(() => {
    if (!isOpen) {
      setStep('cart');
      setShippingMethod(null);
      setShippingQuotes([]);
      setSelectedQuote(null);
      setShippingError('');
    }
  }, [isOpen]);

  if (!isOpen) return null;

  const subtotal = cartItems.reduce((sum, item) => sum + item.price * item.quantity, 0);

  const shippingCost = shippingMethod === 'local_gta'
    ? LOCAL_GTA_RATE
    : shippingMethod === 'eshipper' && selectedQuote
      ? selectedQuote.price
      : 0;

  const tax = (subtotal + shippingCost) * BUSINESS.hstRate;
  const total = subtotal + shippingCost + tax;

  const getProduct = (productId: string) => products.find(p => p.id === productId);

  const handleSelectLocalGTA = () => {
    setShippingMethod('local_gta');
    setSelectedQuote(null);
    setShippingError('');
  };

  const handleSelectShipToMe = () => {
    setShippingMethod('eshipper');
    if (shippingQuotes.length === 0) {
      handleFetchRates();
    }
  };

  const canPlaceOrder = shippingMethod === 'local_gta' || (shippingMethod === 'eshipper' && selectedQuote);

  const handlePlaceOrder = async () => {
    if (!canPlaceOrder) return;

    setIsSubmitting(true);
    try {
      const number = await createOrder({
        retailerId: retailerUser.retailerId,
        retailerName: retailerUser.businessName,
        retailerEmail: retailerUser.email,
        items: cartItems,
        subtotal,
        shipping: shippingCost,
        tax: Math.round(tax * 100) / 100,
        shippingAddress: address,
        shippingDetails: {
          method: shippingMethod!,
          amount: shippingCost,
          carrierName: shippingMethod === 'local_gta' ? 'Local GTA Delivery' : selectedQuote?.carrierName,
          serviceName: shippingMethod === 'local_gta' ? 'Local Delivery' : selectedQuote?.serviceName,
          deliveryDays: shippingMethod === 'local_gta' ? 2 : selectedQuote?.deliveryDays,
        },
      });
      setOrderNumber(number);
      setStep('confirm');
      onClearCart();

      // Send order confirmation email (non-blocking)
      const itemsSummary = cartItems
        .map(item => `${item.name} x${item.quantity} — $${(item.price * item.quantity).toFixed(2)}`)
        .join('\n');

      sendOrderConfirmation({
        to_email: retailerUser.email,
        to_name: retailerUser.contactName,
        order_number: number,
        order_total: `$${total.toFixed(2)}`,
        items_summary: itemsSummary,
      }).catch(err => console.error('Order confirmation email failed:', err));
    } catch (err: any) {
      setOrderError(err.message || 'Failed to place order. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-brand-dark/90 backdrop-blur-sm" onClick={onClose} />
      <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="bg-brand-light-bg px-6 py-4 border-b border-gray-100 flex items-center justify-between flex-shrink-0">
          <div>
            <h2 className="font-display font-bold text-lg text-brand-dark">
              {step === 'cart' && 'Your Cart'}
              {step === 'shipping' && 'Shipping & Payment'}
              {step === 'confirm' && 'Order Confirmed!'}
            </h2>
            {step !== 'confirm' && (
              <p className="text-xs text-brand-text-muted mt-0.5">
                {cartItems.length} item{cartItems.length !== 1 ? 's' : ''} &middot; {retailerUser.businessName}
              </p>
            )}
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 transition-colors">
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Body */}
        <div className="overflow-y-auto flex-1 p-6">
          {/* ─── STEP: CART ─── */}
          {step === 'cart' && (
            <>
              {cartItems.length === 0 ? (
                <div className="text-center py-12">
                  <p className="text-brand-text-muted mb-2">Your cart is empty.</p>
                  <button onClick={onClose} className="text-sm font-semibold text-brand-red hover:text-brand-red-dark transition-colors">
                    Continue Shopping
                  </button>
                </div>
              ) : (
                <div className="space-y-4">
                  {cartItems.map((item) => {
                    const product = getProduct(item.productId);
                    return (
                      <div key={item.productId} className="flex items-center gap-4 bg-brand-light-bg/50 rounded-xl p-4">
                        <div className="flex-1 min-w-0">
                          <h4 className="font-display font-bold text-sm text-brand-dark truncate">{item.name}</h4>
                          <p className="text-xs text-brand-text-muted">SKU: {item.sku} &middot; ${item.price.toFixed(2)} each</p>
                        </div>
                        <div className="flex items-center gap-2">
                          <button
                            onClick={() => onUpdateQuantity(item.productId, Math.max(1, item.quantity - 1))}
                            className="w-7 h-7 rounded-md border border-gray-200 flex items-center justify-center text-brand-text-muted hover:border-brand-red hover:text-brand-red transition-colors"
                          >
                            <Minus className="w-3 h-3" />
                          </button>
                          <span className="text-sm font-semibold text-brand-dark w-8 text-center">{item.quantity}</span>
                          <button
                            onClick={() => onUpdateQuantity(item.productId, Math.min(product?.stock || 999, item.quantity + 1))}
                            className="w-7 h-7 rounded-md border border-gray-200 flex items-center justify-center text-brand-text-muted hover:border-brand-red hover:text-brand-red transition-colors"
                          >
                            <Plus className="w-3 h-3" />
                          </button>
                        </div>
                        <div className="text-right w-20">
                          <div className="text-sm font-bold text-brand-dark">${(item.price * item.quantity).toFixed(2)}</div>
                        </div>
                        <button
                          onClick={() => onRemoveItem(item.productId)}
                          className="text-gray-300 hover:text-red-500 transition-colors"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    );
                  })}
                </div>
              )}
            </>
          )}

          {/* ─── STEP: SHIPPING ─── */}
          {step === 'shipping' && (
            <div className="space-y-4">
              {/* Address Form */}
              <div className="flex items-center gap-2 text-brand-red mb-2">
                <MapPin className="w-4 h-4" />
                <span className="text-xs font-bold uppercase tracking-wider">Delivery Address</span>
              </div>
              <input
                type="text"
                placeholder="Street Address"
                value={address.street}
                onChange={(e) => { setAddress({ ...address, street: e.target.value }); setShippingQuotes([]); setShippingMethod(null); }}
                className="w-full border border-gray-200 rounded-[8px] px-4 py-3 text-sm text-brand-dark placeholder:text-brand-text-muted/50 focus:ring-2 focus:ring-brand-red focus:border-brand-red outline-none"
                required
              />
              <div className="grid grid-cols-2 gap-3">
                <input
                  type="text"
                  placeholder="City"
                  value={address.city}
                  onChange={(e) => { setAddress({ ...address, city: e.target.value }); setShippingQuotes([]); setShippingMethod(null); }}
                  className="w-full border border-gray-200 rounded-[8px] px-4 py-3 text-sm text-brand-dark placeholder:text-brand-text-muted/50 focus:ring-2 focus:ring-brand-red focus:border-brand-red outline-none"
                  required
                />
                <select
                  value={address.province}
                  onChange={(e) => { setAddress({ ...address, province: e.target.value }); setShippingQuotes([]); setShippingMethod(null); }}
                  className="w-full border border-gray-200 rounded-[8px] px-4 py-3 text-sm text-brand-dark focus:ring-2 focus:ring-brand-red focus:border-brand-red outline-none"
                >
                  {['ON','QC','BC','AB','MB','SK','NS','NB','NL','PE','NT','YT','NU'].map(p => (
                    <option key={p} value={p}>{p}</option>
                  ))}
                </select>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <input
                  type="text"
                  placeholder="Postal Code"
                  value={address.postalCode}
                  onChange={(e) => { setAddress({ ...address, postalCode: e.target.value }); setShippingQuotes([]); setShippingMethod(null); }}
                  className="w-full border border-gray-200 rounded-[8px] px-4 py-3 text-sm text-brand-dark placeholder:text-brand-text-muted/50 focus:ring-2 focus:ring-brand-red focus:border-brand-red outline-none"
                  required
                />
                <input
                  type="text"
                  value="Canada"
                  disabled
                  className="w-full border border-gray-200 rounded-[8px] px-4 py-3 text-sm text-brand-text-muted bg-gray-50"
                />
              </div>

              {/* ─── Shipping Method Selection ─── */}
              <div className="mt-6">
                <div className="flex items-center gap-2 text-brand-red mb-3">
                  <Truck className="w-4 h-4" />
                  <span className="text-xs font-bold uppercase tracking-wider">Shipping Method</span>
                </div>

                {/* Option 1: Local GTA Delivery */}
                <button
                  onClick={handleSelectLocalGTA}
                  className={`w-full rounded-xl p-4 flex items-center gap-3 text-left transition-all border-2 mb-2 ${
                    shippingMethod === 'local_gta'
                      ? 'border-brand-red bg-brand-red/5'
                      : 'border-gray-100 bg-brand-light-bg hover:border-gray-300'
                  }`}
                >
                  <div className={`w-4 h-4 rounded-full border-2 flex items-center justify-center flex-shrink-0 ${
                    shippingMethod === 'local_gta' ? 'border-brand-red' : 'border-gray-300'
                  }`}>
                    {shippingMethod === 'local_gta' && <div className="w-2 h-2 rounded-full bg-brand-red" />}
                  </div>
                  <Package className="w-5 h-5 text-brand-green flex-shrink-0" />
                  <div className="flex-1">
                    <div className="text-sm font-semibold text-brand-dark">Local GTA Delivery</div>
                    <div className="text-xs text-brand-text-muted">Manual delivery within Greater Toronto Area &middot; 1-2 business days</div>
                  </div>
                  <div className="text-sm font-bold text-brand-dark">${LOCAL_GTA_RATE.toFixed(2)}</div>
                </button>

                {/* Option 2: Ship to Me (eShipper) */}
                <button
                  onClick={handleSelectShipToMe}
                  disabled={!isAddressComplete}
                  className={`w-full rounded-xl p-4 flex items-center gap-3 text-left transition-all border-2 ${
                    shippingMethod === 'eshipper'
                      ? 'border-brand-red bg-brand-red/5'
                      : 'border-gray-100 bg-brand-light-bg hover:border-gray-300'
                  } disabled:opacity-40 disabled:cursor-not-allowed`}
                >
                  <div className={`w-4 h-4 rounded-full border-2 flex items-center justify-center flex-shrink-0 ${
                    shippingMethod === 'eshipper' ? 'border-brand-red' : 'border-gray-300'
                  }`}>
                    {shippingMethod === 'eshipper' && <div className="w-2 h-2 rounded-full bg-brand-red" />}
                  </div>
                  <Truck className="w-5 h-5 text-brand-navy flex-shrink-0" />
                  <div className="flex-1">
                    <div className="text-sm font-semibold text-brand-dark">Ship to Me</div>
                    <div className="text-xs text-brand-text-muted">
                      {isAddressComplete ? 'Carrier rates for your address' : 'Fill in address to see rates'}
                    </div>
                  </div>
                  {isFetchingRates && <Loader2 className="w-4 h-4 text-brand-red animate-spin flex-shrink-0" />}
                </button>

                {/* eShipper Quotes (nested under Ship to Me) */}
                {shippingMethod === 'eshipper' && (
                  <div className="ml-8 mt-2 space-y-2">
                    {isFetchingRates && (
                      <div className="bg-brand-light-bg rounded-lg p-4 flex items-center justify-center gap-3">
                        <Loader2 className="w-4 h-4 text-brand-red animate-spin" />
                        <span className="text-sm text-brand-text-muted">Fetching carrier rates...</span>
                      </div>
                    )}

                    {shippingError && (
                      <div className="bg-red-50 border border-red-200 rounded-lg p-3">
                        <div className="flex items-start gap-2 mb-2">
                          <AlertCircle className="w-4 h-4 text-red-500 flex-shrink-0 mt-0.5" />
                          <p className="text-sm text-red-700">{shippingError}</p>
                        </div>
                        <button
                          onClick={handleFetchRates}
                          className="text-xs font-semibold text-red-600 hover:text-red-800 flex items-center gap-1"
                        >
                          <RefreshCw className="w-3 h-3" /> Try Again
                        </button>
                      </div>
                    )}

                    {shippingQuotes.map((quote, idx) => (
                      <button
                        key={idx}
                        onClick={() => setSelectedQuote(quote)}
                        className={`w-full rounded-lg p-3 flex items-center gap-3 text-left transition-all border ${
                          selectedQuote?.serviceId === quote.serviceId
                            ? 'border-brand-red bg-brand-red/5'
                            : 'border-gray-200 bg-white hover:border-gray-300'
                        }`}
                      >
                        <div className={`w-3 h-3 rounded-full border-2 flex items-center justify-center flex-shrink-0 ${
                          selectedQuote?.serviceId === quote.serviceId ? 'border-brand-red' : 'border-gray-300'
                        }`}>
                          {selectedQuote?.serviceId === quote.serviceId && (
                            <div className="w-1.5 h-1.5 rounded-full bg-brand-red" />
                          )}
                        </div>
                        <div className="flex-1">
                          <div className="text-sm font-medium text-brand-dark">{quote.carrierName}</div>
                          <div className="text-xs text-brand-text-muted">
                            {quote.serviceName}
                            {quote.deliveryDays && ` · ${quote.deliveryDays} day${quote.deliveryDays !== 1 ? 's' : ''}`}
                          </div>
                        </div>
                        <div className="text-sm font-bold text-brand-dark">${quote.price.toFixed(2)}</div>
                      </button>
                    ))}

                    {shippingQuotes.length > 0 && (
                      <button
                        onClick={handleFetchRates}
                        className="text-xs text-brand-text-muted hover:text-brand-red flex items-center gap-1 transition-colors mt-1"
                      >
                        <RefreshCw className="w-3 h-3" /> Refresh rates
                      </button>
                    )}
                  </div>
                )}
              </div>

              {/* Order Summary (show when shipping method selected) */}
              {canPlaceOrder && (
                <div className="mt-6 border-t border-gray-100 pt-4 space-y-2">
                  <div className="flex justify-between text-sm text-brand-text-muted">
                    <span>Subtotal ({cartItems.reduce((s, i) => s + i.quantity, 0)} items)</span>
                    <span>${subtotal.toFixed(2)}</span>
                  </div>
                  <div className="flex justify-between text-sm text-brand-text-muted">
                    <span>Shipping ({shippingMethod === 'local_gta' ? 'Local GTA' : selectedQuote?.serviceName})</span>
                    <span>${shippingCost.toFixed(2)}</span>
                  </div>
                  <div className="flex justify-between text-sm text-brand-text-muted">
                    <span>HST (13%)</span>
                    <span>${tax.toFixed(2)}</span>
                  </div>
                  <div className="flex justify-between text-base font-bold text-brand-dark pt-2 border-t border-gray-100">
                    <span>Total</span>
                    <span>${total.toFixed(2)}</span>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* ─── STEP: CONFIRM ─── */}
          {step === 'confirm' && (
            <div className="text-center py-6">
              <div className="w-16 h-16 bg-brand-green/10 rounded-full flex items-center justify-center mx-auto mb-5">
                <CheckCircle className="w-8 h-8 text-brand-green" />
              </div>
              <h3 className="font-display font-bold text-2xl text-brand-dark mb-2">Order Placed!</h3>
              <p className="text-brand-text-muted mb-1">Your order number is:</p>
              <div className="inline-block bg-brand-light-bg rounded-lg px-5 py-2.5 mb-6">
                <span className="font-display font-bold text-xl text-brand-red">{orderNumber}</span>
              </div>

              <div className="bg-brand-gold/10 border border-brand-gold/30 rounded-xl p-5 text-left mb-6">
                <h4 className="font-display font-bold text-sm text-brand-dark mb-2">Payment Instructions</h4>
                <p className="text-sm text-brand-text-muted leading-relaxed">
                  Send an <strong className="text-brand-dark">Interac e-Transfer</strong> to{' '}
                  <strong className="text-brand-dark">{BUSINESS.eTransferEmail}</strong>
                </p>
                <p className="text-sm text-brand-text-muted mt-1">
                  Include <strong className="text-brand-dark">{orderNumber}</strong> in the memo/message field.
                </p>
              </div>

              <p className="text-xs text-brand-text-muted">
                We'll process your order once payment is confirmed. You'll receive email updates on your order status.
              </p>
            </div>
          )}
        </div>

        {/* Footer Actions */}
        {step !== 'confirm' && cartItems.length > 0 && (
          <div className="px-6 py-4 border-t border-gray-100 flex-shrink-0">
            {step === 'cart' && (
              <div className="flex items-center justify-between">
                <div>
                  <span className="text-sm text-brand-text-muted">Subtotal: </span>
                  <span className="text-lg font-bold text-brand-dark">${subtotal.toFixed(2)}</span>
                </div>
                <button
                  onClick={() => setStep('shipping')}
                  className="bg-brand-red text-white px-8 py-3 rounded-[8px] font-semibold text-sm hover:bg-brand-red-dark transition-all shadow-md shadow-brand-red/25 hover:-translate-y-0.5"
                >
                  Proceed to Checkout
                </button>
              </div>
            )}
            {step === 'shipping' && (
              <div className="flex items-center justify-between gap-3">
                <button
                  onClick={() => setStep('cart')}
                  className="text-sm font-semibold text-brand-text-muted hover:text-brand-dark transition-colors"
                >
                  Back to Cart
                </button>
                <button
                  onClick={handlePlaceOrder}
                  disabled={!canPlaceOrder || isSubmitting}
                  className="bg-brand-gold text-brand-dark px-8 py-3 rounded-[8px] font-bold text-sm hover:bg-brand-gold-light transition-all shadow-md disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                >
                  {isSubmitting ? (
                    <>
                      <Loader2 className="w-4 h-4 animate-spin" />
                      Placing Order...
                    </>
                  ) : (
                    `Place Order — $${total.toFixed(2)}`
                  )}
                </button>
                {orderError && (
                  <div className="flex items-start gap-2 bg-red-50 border border-red-200 rounded-lg p-3 mt-3">
                    <AlertCircle className="w-4 h-4 text-red-500 flex-shrink-0 mt-0.5" />
                    <p className="text-red-700 text-sm">{orderError}</p>
                  </div>
                )}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default CheckoutModal;
